import React from 'react';
import PropTypes from 'prop-types';
import { PageLevelMessage } from '@americanexpress/dls-react';
import { parseHtml } from '../../utils/util';
import { getIcon } from './Icons';

/**
 * DlsNotification wraps the DLS PageLevelMessage component, configurable from JSON.
 *
 * Visibility is driven by a key in formSubmissionPayload:
 *   - When formSubmissionPayload[visibilityKey] is truthy  → notification is shown
 *   - When formSubmissionPayload[visibilityKey] is falsy   → notification is hidden
 * If visibilityKey is omitted the notification is always shown (controlled by fieldIndicator).
 *
 * The message text can be static (fieldValue) or dynamic — pulled from
 * formSubmissionPayload[messageKey] — allowing API responses to populate it.
 *
 * JSON config example:
 * {
 *   "fieldType": "dlsNotification",
 *   "fieldId": "companyNotification",
 *   "cssProperties": { "style": "col-md-12 pad-2-t" },
 *   "fieldValue": "Static message text",
 *   "status": "error",
 *   "showStateIcon": true,
 *   "ariaLive": "assertive",
 *   "visibilityKey": "showNotification",
 *   "messageKey": "notificationMessage",
 *  "iconName": "IconInfo",
 *  "dismissable": false
 * }
 */
const DlsNotification = (props) => {
  const {
    cssProperties,
    fieldId,
    fieldValue,
    ariaLive = 'assertive',
    iconName = 'IconInfo',
    dismissable = false,
  } = props;

  return (
    <div className={cssProperties?.style}>
      <PageLevelMessage
        icon={getIcon(iconName)}
        type="neutral"
        dismissible={dismissable}
        aria-live={ariaLive}
        id={fieldId}
      >
        {fieldValue ? parseHtml(fieldValue) : null}
      </PageLevelMessage>
    </div>
  );
};

DlsNotification.propTypes = {
  cssProperties: PropTypes.shape({
    style: PropTypes.string,
  }),
  fieldId: PropTypes.string,
  fieldValue: PropTypes.string,
  ariaLive: PropTypes.oneOf(['assertive', 'polite', 'off']),
  iconName: PropTypes.string,
  // eslint-disable-next-line react/boolean-prop-naming -- prop name matches existing JSON API, cannot rename
  dismissable: PropTypes.bool,
};

export default DlsNotification;
